import React, { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { keyframes } from '@mui/material'
import { styled } from '@mui/material/styles'
import { monoLabelSx, STUDIO_ACCENT } from '../shared'
import { APP_NAME } from '@/utils/constants'
import { LogoMark } from '@/features/album/components/EgowallLogo'

/** Shared load black — keep in sync with Studio3DLayout / Stage. */
export const STUDIO_LOAD_BG = '#000000'

const spin = keyframes`to { transform: rotate(360deg); }`

const Veil = styled('div')({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  width: '100vw',
  minHeight: '100vh',
  height: '100dvh',
  zIndex: 10000,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 16,
  margin: 0,
  padding: 0,
  boxSizing: 'border-box',
  backgroundColor: STUDIO_LOAD_BG,
  backgroundImage: 'none',
  color: '#f4f3f6',
  fontFamily: 'var(--ds-font)',
})

const Spinner = styled('div')({
  width: 28,
  height: 28,
  borderRadius: '50%',
  border: '2.5px solid rgba(244,243,246,0.14)',
  borderTopColor: STUDIO_ACCENT.violet,
  animation: `${spin} 0.8s linear infinite`,
  background: 'transparent',
  boxSizing: 'border-box',
})

/** Spectrum "e" mark — shared with Present Hall brand chrome. */
export function EgowallGlyph({ small }: { small?: boolean }) {
  return <LogoMark size={small ? 24 : 26} />
}

const Brand = styled('div')({
  fontSize: 17,
  fontWeight: 750,
  letterSpacing: '-0.02em',
  color: '#f4f3f6',
  background: 'transparent',
  margin: 0,
})

const Status = styled('div')({
  ...monoLabelSx,
  fontSize: 9,
  color: 'rgba(244,243,246,0.55)',
  background: 'transparent',
  margin: 0,
})

export interface StudioLoadingVeilProps {
  /** Mono status line under the spinner. */
  label?: string
}

/**
 * Full-bleed loading veil — portals to `document.body` and paints
 * `html`/`body` the same black so CssBaseline's white canvas can't fringe
 * on overscroll / first paint.
 */
export function StudioLoadingVeil({ label = 'BUILDING THE GALLERY…' }: StudioLoadingVeilProps) {
  useEffect(() => {
    const html = document.documentElement
    const body = document.body
    const root = document.getElementById('root')
    const prev = {
      htmlBg: html.style.backgroundColor,
      bodyBg: body.style.backgroundColor,
      rootBg: root?.style.backgroundColor ?? '',
    }
    html.style.backgroundColor = STUDIO_LOAD_BG
    body.style.backgroundColor = STUDIO_LOAD_BG
    if (root) root.style.backgroundColor = STUDIO_LOAD_BG
    return () => {
      html.style.backgroundColor = prev.htmlBg
      body.style.backgroundColor = prev.bodyBg
      if (root) root.style.backgroundColor = prev.rootBg
    }
  }, [])

  const node = (
    <Veil role='status' aria-live='polite' aria-busy='true'>
      <EgowallGlyph />
      <Brand>{APP_NAME}</Brand>
      <Spinner />
      <Status>{label}</Status>
    </Veil>
  )

  if (typeof document === 'undefined') return node
  return createPortal(node, document.body)
}
